import { message, Tabs } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useEffect, useState } from 'react';
import {
	AdminProductFilters,
	useProduct,
} from '../../common/hooks/useProduct';
import BVApplicationTemplate from '../../common/templates/BVApplicationTemplate/bv-application-template';

import { LimitationType } from '../../api-client';
import BVCustomerPricesImport from '../../common/components/AdminComponents/BVCustomerProductsImport/bv-customer-prices-import';
import BVLimitationsImport from '../../common/components/AdminComponents/BVLimitationsImport/bv-limitations-import';
import BVLimitationsTable from '../../common/components/AdminComponents/BVLimitationsTable/bv-limitations-table';
import BVLimitationsValueTable from '../../common/components/AdminComponents/BVLimitationsValuesTable/bv-limitations-values-table';
import BVOrdersExportTable from '../../common/components/AdminComponents/BVorderSapTable/bv-order-sap-table';
import BVProductsImport from '../../common/components/AdminComponents/BVProductsImport/bv-products-import';
import BVProductsTable from '../../common/components/AdminComponents/BVProductsTable/bv-products-table';
import { useCompleteDialog } from '../../common/components/BVDrawers/BVCheckoutDrawer/components/ShoppingCompleteDialog/hooks/useCompleteDialog';
import { useLimitations } from '../../common/hooks/useLimitations';
import { useOrder } from '../../common/hooks/useOrder';
import './admin-page.scss';

const AdminPage = () => {
	const { TabPane } = Tabs;
	const [pricesFileList, setPricesFileList] = useState<UploadFile[]>([]);
	const [productsFileList, setProductsFileList] = useState<UploadFile[]>([]);
	const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(
		undefined,
	);
	const [page, setPage] = useState(1);
	const [productFilters, setProductFilters] = useState<AdminProductFilters>({
		sapCode: '',
		catalogCode: '',
		aestheticMotif: '',
		aestheticLine: '',
	});

	const [orderReason, setOrderReason] = useState<string>();

	const {
		uploadCustomerPricesExcel,
		uploadCustomerPricesLoading,
		uploadCustomerPricesSuccess,
		resetCustomerPricesUploadState,

		adminProducts,
		totalAdminProductsCount,
		adminProductsLoading,
		handleAdminProducts,

		uploadProductsExcel,
		uploadProductsLoading,
		uploadProductsSuccess,
		uploadProductsError,
		resetProductsUploadState,
		exportAdminProductsLoading,
		handleExportAdminProducts,
	} = useProduct();

	const { orderReasons, handleOrderReasons, orderReasonsLoading } =
		useCompleteDialog();

	const {
		adminOrders,
		handleAdminOrdersByReasonCode,
		adminOrdersLoading,
		exportAdminOrdersLoading,
		handleExportAdminOrdersByReasonCode,
	} = useOrder();

	const {
		limitations,
		limitationsLoading,
		handleLimitationsBySapCode,
		removeLimitation,
		removeLimitationLoading,
		exportLimitations,
		exportLimitationsLoading,
		limitationValues,
		limitationValuesLoading,
		handleLimitationValuesByType,
		importLimitations,
		importLimitationsLoading,
		importLimitationsSuccess,
		resetImportLimitationsState,
		exportLimitationsImportTemplate,
		exportLimitationsTemplateLoading,
	} = useLimitations();

	const [limitationType, setLimitationType] = useState<LimitationType>(
		'SalesChannel' as LimitationType,
	);

	const [limitationsFileList, setLimitationsFileList] = useState<UploadFile[]>(
		[],
	);

	useEffect(() => {
		if (importLimitationsSuccess) {
			message.success('Limitations imported correctly!');
			setLimitationsFileList([]);
			resetImportLimitationsState();
		}
	}, [importLimitationsSuccess]);

	useEffect(() => {
		handleLimitationValuesByType(limitationType);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		handleOrderReasons();
	}, []);

	useEffect(() => {
		if (orderReasonsLoading || !orderReasons || orderReasons.length === 0)
			return;
		setOrderReason(orderReasons[0].code ?? '');
	}, [orderReasons, orderReasonsLoading]);

	useEffect(() => {
		if (!orderReason) return;
		handleAdminOrdersByReasonCode(orderReason);
	}, [orderReason]);

	useEffect(() => {
		handleAdminProducts(page, enabledFilter, productFilters);
	}, [enabledFilter, page, productFilters]);

	useEffect(() => {
		if (uploadCustomerPricesSuccess) {
			message.success('Customer prices uploaded correctly!');
			setPricesFileList([]);
			resetCustomerPricesUploadState();
		}
	}, [uploadCustomerPricesSuccess]);

	useEffect(() => {
		if (uploadProductsSuccess) {
			message.success('Products imported correctly!');
			setProductsFileList([]);
			resetProductsUploadState();
			handleAdminProducts(page, enabledFilter, productFilters);
		}
	}, [uploadProductsSuccess]);

	const handleProductFilterChange = (
		key: keyof AdminProductFilters,
		value: string,
	) => {
		setPage(1);
		setProductFilters((current) => ({
			...current,
			[key]: value,
		}));
	};

	const handleEnabledFilterChange = (value: boolean | undefined) => {
		setPage(1);
		setEnabledFilter(value);
	};

	return (
		<BVApplicationTemplate>
			<div className="admin-container">
				<Tabs defaultActiveKey="catalog">
					<TabPane tab="Products" key="catalog">
						<BVProductsTable
							products={adminProducts ?? []}
							loading={adminProductsLoading}
							onRefresh={async () =>
								await handleAdminProducts(page, enabledFilter, productFilters)
							}
							filters={productFilters}
							onFilterChange={handleProductFilterChange}
							enabledFilter={enabledFilter}
							onEnabledFilterChange={handleEnabledFilterChange}
							loadingExport={exportAdminProductsLoading}
							onExport={async () => {
								await handleExportAdminProducts(
									enabledFilter ?? undefined,
									productFilters,
								);
							}}
							page={page}
							setPage={setPage}
							productsCount={totalAdminProductsCount ?? 0}
						/>
					</TabPane>

					<TabPane tab="Orders" key="orders">
						<BVOrdersExportTable
							orderReasons={orderReasons ?? []}
							setOrderReasonCode={setOrderReason}
							loading={!!adminOrdersLoading || !!orderReasonsLoading}
							orders={adminOrders ?? []}
							onRefresh={async () =>
								await handleAdminOrdersByReasonCode(orderReason ?? '')
							}
							loadingExport={exportAdminOrdersLoading}
							onExport={async () => {
								await handleExportAdminOrdersByReasonCode(orderReason ?? '');
							}}
						/>
					</TabPane>

					<TabPane tab="Limitations" key="limitations">
						<BVLimitationsTable
							loading={!!limitationsLoading}
							limitations={limitations ?? []}
							onRefresh={handleLimitationsBySapCode}
							onRemove={removeLimitation}
							loadingRemove={!!removeLimitationLoading}
							onExport={exportLimitations}
							loadingExport={!!exportLimitationsLoading}
						/>
						<br />
						<BVLimitationsValueTable
							loading={!!limitationValuesLoading}
							values={limitationValues ?? []}
							selectedType={limitationType}
							onTypeChange={async (t) => {
								setLimitationType(t);
								await handleLimitationValuesByType(t);
							}}
							onRefresh={async () => {
								await handleLimitationValuesByType(limitationType);
							}}
						/>
					</TabPane>

					<TabPane tab="Imports area" key="imports">
						<BVProductsImport
							fileList={productsFileList}
							setFileList={setProductsFileList}
							uploadProductsExcel={uploadProductsExcel}
							loading={!!uploadProductsLoading}
							error={uploadProductsError}
							resetProductsUploadState={resetProductsUploadState}
						/>
						<br />
						<BVCustomerPricesImport
							fileList={pricesFileList}
							setFileList={setPricesFileList}
							uploadCustomerPricesExcel={uploadCustomerPricesExcel}
							loading={!!uploadCustomerPricesLoading}
							resetCustomerPricesUploadState={resetCustomerPricesUploadState}
						/>
						<br />
						<BVLimitationsImport
							fileList={limitationsFileList}
							setFileList={setLimitationsFileList}
							importLimitations={importLimitations}
							loading={importLimitationsLoading}
							resetImportLimitationsState={resetImportLimitationsState}
							exportLimitationsImportTemplate={exportLimitationsImportTemplate}
							exportLimitationsTemplateLoading={
								exportLimitationsTemplateLoading
							}
						/>
						;
					</TabPane>
				</Tabs>
			</div>
		</BVApplicationTemplate>
	);
};

export default AdminPage;
